import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Copy, Loader2, RefreshCw, Smartphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useProject } from "@/lib/workflow/useProject";
import { appUrl, buildLiveSnapshot, emptyExtras, type LiveExtras } from "@/lib/workflow/liveSnapshot";

/** The Guest, Door Check and Booth Leads app pages are live, so the Publish screen is shown to Premium organisers. */
export const LIVE_APPS_READY = true;

const field = "w-full rounded-md border bg-background px-3 py-2 text-sm";
const ROLE_TITLE = { guest: "Guest app", door: "Door Check app", booth: "Booth Leads app" } as const;
const ROLE_HELP = {
  guest: "Anyone with this link can see the programme, venue and announcements. They cannot see the guest list.",
  door: "Give this only to door staff. It shows the guest list and lets them check guests in.",
  booth: "One link per exhibitor. Leads captured with it belong to that exhibitor. You see only the count.",
} as const;
type Role = keyof typeof ROLE_TITLE;
const rememberKey = (passKey: string) => `fdp-live-${passKey.slice(0, 12)}`;

function Qr({ text }: { text: string }) {
  const [src, setSrc] = useState("");
  useEffect(() => { let on = true; QRCode.toDataURL(text, { margin: 1, width: 160 }).then((u) => { if (on) setSrc(u); }).catch(() => {}); return () => { on = false; }; }, [text]);
  return src ? <img src={src} alt="QR code for this link" width={96} height={96} className="rounded border bg-white" /> : <div className="h-24 w-24" />;
}

/**
 * Event Suite Premium: publish the open project to the Guest, Door Check and Booth Leads apps. Publishing saves a snapshot
 * on our server and creates one link per role; pressing Update again replaces the snapshot and the apps pick it up.
 */
export function PublishEvent() {
  const [project] = useProject();
  const utils = trpc.useUtils();
  const list = trpc.eventSuite.live.list.useQuery(undefined, { retry: false });
  const publish = trpc.eventSuite.live.publish.useMutation();
  const addLink = trpc.eventSuite.live.addLink.useMutation();
  const revoke = trpc.eventSuite.live.revokeLink.useMutation();
  const rotate = trpc.eventSuite.live.rotateLink.useMutation();
  const remove = trpc.eventSuite.live.remove.useMutation();
  const [eventId, setEventId] = useState<string | null>(null);
  const [extras, setExtras] = useState<LiveExtras>(emptyExtras());
  const [boothName, setBoothName] = useState("");
  const [busy, setBusy] = useState(false);
  const key = rememberKey(project.passKey);

  // the event this project was last published as (kept in this browser only)
  useEffect(() => {
    try { setEventId(localStorage.getItem(key)); } catch { setEventId(null); }
  }, [key]);
  const known = list.data?.events.find((e) => e.id === eventId) ?? null;
  const detail = trpc.eventSuite.live.detail.useQuery({ eventId: known?.id ?? "" }, { enabled: !!known, retry: false });
  const opened = trpc.eventSuite.live.open.useQuery({ eventId: known?.id ?? "" }, { enabled: !!known, retry: false });
  useEffect(() => {
    if (!opened.data) return;
    const e = opened.data.event;
    setExtras({ venue: e.venue, address: e.address, mapUrl: e.mapUrl, notes: e.notes, announcements: opened.data.announcements });
  }, [opened.data]);

  if (list.isError) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const refresh = async () => { await Promise.all([utils.eventSuite.live.list.invalidate(), utils.eventSuite.live.detail.invalidate(), utils.eventSuite.live.open.invalidate(), utils.eventSuite.storage.list.invalidate()]); };
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try { await fn(); await refresh(); if (ok) toast.success(ok); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  const doPublish = () => run(async () => {
    const r = await publish.mutateAsync({ eventId: known?.id, snapshot: buildLiveSnapshot(project, extras) });
    try { localStorage.setItem(key, r.eventId); } catch { /* the event can still be managed from this tab */ }
    setEventId(r.eventId);
  }, known ? "Updated. The apps show the change within a minute." : "Published. Share the links below.");

  const copy = (text: string) => { void navigator.clipboard?.writeText(text).then(() => toast.success("Link copied"), () => toast.error("Could not copy. Select the link and copy it.")); };
  const set = (patch: Partial<LiveExtras>) => setExtras((x) => ({ ...x, ...patch }));
  const links = detail.data?.links.filter((l) => !l.revoked) ?? [];

  return (
    <section className="mt-3 rounded-lg border bg-card p-4 text-sm" data-testid="publish-event">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold flex items-center gap-1"><Smartphone className="h-4 w-4" />Event apps</h3>
        <span className="text-xs text-muted-foreground">Publish this project to the Guest, Door Check and Booth Leads apps.</span>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <label className="text-xs">Venue<input className={field} value={extras.venue} maxLength={160} onChange={(e) => set({ venue: e.target.value })} /></label>
        <label className="text-xs">Address<input className={field} value={extras.address} maxLength={240} onChange={(e) => set({ address: e.target.value })} /></label>
        <label className="text-xs">Map link (https)<input className={field} value={extras.mapUrl} maxLength={500} onChange={(e) => set({ mapUrl: e.target.value })} /></label>
        <label className="text-xs">Notes for guests<input className={field} value={extras.notes} maxLength={1000} onChange={(e) => set({ notes: e.target.value })} /></label>
      </div>

      <div className="mt-3">
        <div className="text-xs font-medium">Announcements</div>
        {extras.announcements.map((a, i) => (
          <div key={a.id} className="mt-1 flex gap-2">
            <input className={field} value={a.text} maxLength={600} placeholder="Short message for guests" onChange={(e) => set({ announcements: extras.announcements.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })} />
            <Button size="sm" variant="ghost" aria-label="Remove announcement" onClick={() => set({ announcements: extras.announcements.filter((_, j) => j !== i) })}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
        {extras.announcements.length < 20 && (
          <Button size="sm" variant="outline" className="mt-1" onClick={() => set({ announcements: [...extras.announcements, { id: `a${Date.now().toString(36)}`, at: new Date().toISOString(), text: "" }] })}>Add announcement</Button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => void doPublish()} disabled={busy} data-testid="publish-go">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          {known ? "Update published event" : "Publish event"}
        </Button>
        {known && <span className="text-xs text-muted-foreground">Version {known.version}, {Math.max(1, Math.round(known.sizeBytes / 1000))} KB</span>}
        {known && (
          <Button size="sm" variant="ghost" className="ml-auto" disabled={busy} onClick={() => {
            if (!window.confirm("Delete this published event? The links stop working and its check-ins and booth leads are erased. This cannot be undone.")) return;
            void run(async () => { await remove.mutateAsync({ eventId: known.id }); try { localStorage.removeItem(key); } catch { /* ignore */ } setEventId(null); setExtras(emptyExtras()); }, "Published event deleted");
          }}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Publishing sends the programme and venue, and guest names with their status (no emails, phone numbers or dietary details). Door staff links also carry the key that checks passes.</p>

      {known && detail.data && (
        <div className="mt-4">
          <div className="text-xs text-muted-foreground">{detail.data.checkedIn} checked in so far.</div>
          <ul className="mt-2 space-y-3">
            {links.map((l) => {
              const url = appUrl(origin, l.role as Role, l.token);
              return (
                <li key={l.token} className="flex gap-3 rounded-md border p-3">
                  <Qr text={url} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{ROLE_TITLE[l.role as Role]}{l.label ? `: ${l.label}` : ""}{l.leads !== null && <span className="ml-2 text-xs font-normal text-muted-foreground">{l.leads} lead{l.leads === 1 ? "" : "s"}</span>}</div>
                    <div className="text-xs text-muted-foreground">{ROLE_HELP[l.role as Role]}</div>
                    <div className="mt-1 break-all text-xs">{url}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => copy(url)}><Copy className="h-3.5 w-3.5 mr-1" />Copy link</Button>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => { if (window.confirm("Replace this link? The old one stops working and everyone who has it needs the new one.")) void run(() => rotate.mutateAsync({ token: l.token }), "New link made"); }}>Replace link</Button>
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => { if (window.confirm("Stop this link now? Nobody can use it again.")) void run(() => revoke.mutateAsync({ token: l.token }), "Link stopped"); }}>Stop link</Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <input className={`${field} max-w-xs`} value={boothName} maxLength={80} placeholder="Exhibitor or booth name" onChange={(e) => setBoothName(e.target.value)} />
            <Button size="sm" variant="outline" disabled={busy || !boothName.trim()} onClick={() => void run(async () => { await addLink.mutateAsync({ eventId: known.id, role: "booth", label: boothName }); setBoothName(""); }, "Booth link added")}>Add booth link</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void run(() => addLink.mutateAsync({ eventId: known.id, role: "door" }), "Door link added")}>Add door link</Button>
          </div>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">Kept until you delete it or your Event Suite Premium plan ends. It counts toward your 1 GB.</p>
    </section>
  );
}
